import type { DtoolsDisplaySettings } from "./dtoolsDisplaySettings";
import { isDtoolsInternalImportSystem } from "./dtoolsInternalSystems";
import {
  dtoolsSystemContentKeyAliases,
  normalizeStoredDtoolsVisibilityKey,
  resolveDtoolsSystemContentKey,
} from "./dtoolsProposalContent";
import type { DtoolsReviewRoom, DtoolsReviewSystem } from "./dtoolsStagingTransform";

export type DtoolsSystemVisibilityAudience = "customer" | "staff";

/** All proposal preview surfaces use customer visibility (hidden systems stay hidden). */
export const DTOOLS_CUSTOMER_PREVIEW_VISIBILITY_AUDIENCE: DtoolsSystemVisibilityAudience = "customer";

export function readHiddenOptionKeys(displaySettings?: DtoolsDisplaySettings): Set<string> {
  return new Set(
    (displaySettings?.hiddenOptionKeys ?? []).map(normalizeStoredDtoolsVisibilityKey).filter(Boolean),
  );
}

export function readHiddenLocationKeys(displaySettings?: DtoolsDisplaySettings): Set<string> {
  return new Set(
    (displaySettings?.hiddenLocationKeys ?? []).map(normalizeStoredDtoolsVisibilityKey).filter(Boolean),
  );
}

/**
 * Stable customer-visibility key for a room/location.
 * Prefer D-Tools locationId; fall back to a deterministic room-name slug only when id is missing.
 */
export function resolveDtoolsLocationContentKey(room: {
  locationId?: string | null;
  name?: string | null;
}): string {
  const id = String(room.locationId ?? "").trim();
  if (id) return id;
  const name = String(room.name ?? "").trim() || "Unassigned";
  return `room:${name
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")}`;
}

export function readCustomerExposeInternalOptionKeys(displaySettings?: DtoolsDisplaySettings): Set<string> {
  return new Set(
    (displaySettings?.customerExposeInternalOptionKeys ?? [])
      .map(normalizeStoredDtoolsVisibilityKey)
      .filter(Boolean),
  );
}

export function isDtoolsOptionHidden(
  system: Pick<DtoolsReviewSystem, "systemId" | "name">,
  displaySettings?: DtoolsDisplaySettings,
): boolean {
  const key = resolveDtoolsSystemContentKey(system);
  if (readHiddenOptionKeys(displaySettings).has(key)) return true;
  for (const storedKey of displaySettings?.hiddenOptionKeys ?? []) {
    if (normalizeStoredDtoolsVisibilityKey(storedKey) === key) return true;
    if (dtoolsSystemContentKeyAliases(system).includes(storedKey)) return true;
  }
  return false;
}

/** Whether a system may appear in customer-facing tabs, summary, and PDF output. */
export function isCustomerFacingDtoolsSystem(
  system: Pick<DtoolsReviewSystem, "systemId" | "name">,
  displaySettings?: DtoolsDisplaySettings,
  audience: DtoolsSystemVisibilityAudience = "customer",
): boolean {
  if (audience === "staff") return true;
  const key = resolveDtoolsSystemContentKey(system);
  if (readHiddenOptionKeys(displaySettings).has(key)) return false;
  for (const storedKey of displaySettings?.hiddenOptionKeys ?? []) {
    if (normalizeStoredDtoolsVisibilityKey(storedKey) === key) return false;
    if (dtoolsSystemContentKeyAliases(system).includes(storedKey)) return false;
  }
  if (!isDtoolsInternalImportSystem(system)) return true;
  if (readCustomerExposeInternalOptionKeys(displaySettings).has(key)) return true;
  for (const storedKey of displaySettings?.customerExposeInternalOptionKeys ?? []) {
    if (normalizeStoredDtoolsVisibilityKey(storedKey) === key) return true;
    if (dtoolsSystemContentKeyAliases(system).includes(storedKey)) return true;
  }
  return false;
}

export function toggleDtoolsOptionHidden(
  displaySettings: DtoolsDisplaySettings,
  system: Pick<DtoolsReviewSystem, "systemId" | "name">,
  hidden: boolean,
): DtoolsDisplaySettings {
  const key = resolveDtoolsSystemContentKey(system);
  const aliases = dtoolsSystemContentKeyAliases(system);
  const prevHidden = new Set(
    (displaySettings.hiddenOptionKeys ?? []).map(normalizeStoredDtoolsVisibilityKey).filter(Boolean),
  );
  const prevExpose = new Set(
    (displaySettings.customerExposeInternalOptionKeys ?? [])
      .map(normalizeStoredDtoolsVisibilityKey)
      .filter(Boolean),
  );
  if (hidden) {
    for (const alias of aliases) {
      prevHidden.delete(alias);
      prevHidden.delete(normalizeStoredDtoolsVisibilityKey(alias));
      prevExpose.delete(alias);
      prevExpose.delete(normalizeStoredDtoolsVisibilityKey(alias));
    }
    prevHidden.add(key);
  } else {
    for (const alias of aliases) {
      prevHidden.delete(alias);
      prevHidden.delete(normalizeStoredDtoolsVisibilityKey(alias));
    }
    prevHidden.delete(key);
    if (isDtoolsInternalImportSystem(system)) prevExpose.add(key);
  }
  return {
    ...displaySettings,
    hiddenOptionKeys: [...prevHidden],
    customerExposeInternalOptionKeys: [...prevExpose],
  };
}

/** Default-hide D-Tools import placeholder systems from customer output. */
export function withInternalSystemsHiddenByDefault(
  systems: Array<Pick<DtoolsReviewSystem, "systemId" | "name">>,
  displaySettings: DtoolsDisplaySettings = {},
): DtoolsDisplaySettings {
  let next = displaySettings;
  for (const system of systems) {
    if (!isDtoolsInternalImportSystem(system)) continue;
    const key = resolveDtoolsSystemContentKey(system);
    if (readCustomerExposeInternalOptionKeys(next).has(key)) continue;
    if (!isDtoolsOptionHidden(system, next)) {
      next = toggleDtoolsOptionHidden(next, system, true);
    }
  }
  return next;
}

export function isDtoolsLocationHidden(
  room: Pick<DtoolsReviewRoom, "locationId" | "name">,
  displaySettings?: DtoolsDisplaySettings,
): boolean {
  const key = resolveDtoolsLocationContentKey(room);
  return readHiddenLocationKeys(displaySettings).has(key);
}

/** Whether a room/location may appear in customer-facing tabs, summary, PDF, and totals. */
export function isCustomerFacingDtoolsLocation(
  room: Pick<DtoolsReviewRoom, "locationId" | "name">,
  displaySettings?: DtoolsDisplaySettings,
  audience: DtoolsSystemVisibilityAudience = "customer",
): boolean {
  if (audience === "staff") return true;
  return !isDtoolsLocationHidden(room, displaySettings);
}

export function toggleDtoolsLocationHidden(
  displaySettings: DtoolsDisplaySettings,
  room: Pick<DtoolsReviewRoom, "locationId" | "name">,
  hidden: boolean,
): DtoolsDisplaySettings {
  const key = resolveDtoolsLocationContentKey(room);
  const prevHidden = new Set(
    (displaySettings.hiddenLocationKeys ?? []).map(normalizeStoredDtoolsVisibilityKey).filter(Boolean),
  );
  if (hidden) prevHidden.add(key);
  else prevHidden.delete(key);
  return {
    ...displaySettings,
    hiddenLocationKeys: [...prevHidden],
  };
}

/** Strip rooms that are hidden from the customer; system-level primaries are unchanged. */
export function applyCustomerFacingRoomVisibility(
  system: DtoolsReviewSystem,
  displaySettings?: DtoolsDisplaySettings,
  audience: DtoolsSystemVisibilityAudience = "customer",
): DtoolsReviewSystem {
  if (audience === "staff") return system;
  const rooms = system.rooms.filter((room) => isCustomerFacingDtoolsLocation(room, displaySettings, audience));
  if (rooms.length === system.rooms.length) return system;
  return { ...system, rooms };
}

/**
 * Customer-visible systems with hidden rooms removed.
 * optionIndex preserves the original hierarchy index for tab ids / labels.
 */
export function filterVisibleDtoolsSystems(
  systems: DtoolsReviewSystem[],
  displaySettings?: DtoolsDisplaySettings,
  options?: { audience?: DtoolsSystemVisibilityAudience },
): Array<{ system: DtoolsReviewSystem; optionIndex: number }> {
  const audience = options?.audience ?? "customer";
  return systems.flatMap((system, optionIndex) => {
    if (!isCustomerFacingDtoolsSystem(system, displaySettings, audience)) return [];
    return [{ system: applyCustomerFacingRoomVisibility(system, displaySettings, audience), optionIndex }];
  });
}
